/**
 * PendingApprovalScreen - Displayed when user's account is pending admin approval
 *
 * Shown when:
 * - User has verified their email but not yet approved by admin
 * - User tries to access protected routes while pending
 *
 * Features:
 * - Clear pending status message
 * - Signup date and requested airline display
 * - Expected timeframe (1-2 business days)
 * - Sign out button
 */

import { useEffect, useState } from 'react';
import { Clock, Building, Calendar, Mail, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface ProfileData {
  signup_date: string;
  requested_airline_name: string | null;
  requested_airline_code: string | null;
}

interface ProfileWithAirline {
  created_at: string;
  airlines: { name: string; code: string } | null;
}

export default function PendingApprovalScreen() {
  const { signOut, user } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch profile data for display
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select(`
            created_at,
            requested_airline_id,
            airlines:requested_airline_id (
              name,
              code
            )
          `)
          .eq('user_id', user.id)
          .single<ProfileWithAirline>();

        if (!error && data) {
          setProfileData({
            signup_date: data.created_at,
            requested_airline_name: (data.airlines as { name: string; code: string })?.name || null,
            requested_airline_code: (data.airlines as { name: string; code: string })?.code || null,
          });
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [user?.id]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy via-navy/95 to-steel flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/images/wingmentor-logo.png"
            alt="WingMentor Logo"
            className="h-20 w-auto"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
            <Clock size={40} className="text-amber-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-navy mb-2">Account Pending Approval</h1>

          {/* Description */}
          <p className="text-steel mb-6">
            Your account has been created and is awaiting administrator approval.
            You'll receive an email notification once your account has been reviewed.
          </p>

          {/* Info Cards */}
          {!isLoading && profileData && (
            <div className="space-y-3 mb-6 text-left">
              {/* Signup Date */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar size={18} className="text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Signup Date</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(profileData.signup_date)}
                  </p>
                </div>
              </div>

              {/* Requested Airline */}
              {profileData.requested_airline_name && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Building size={18} className="text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Requested Airline</p>
                    <p className="text-sm font-medium text-gray-900">
                      {profileData.requested_airline_name}
                      {profileData.requested_airline_code && (
                        <span className="text-gray-500 font-normal ml-1">
                          ({profileData.requested_airline_code})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Email */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail size={18} className="text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Expected Timeframe */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Expected timeframe:</strong> Typically 1-2 business days.
              If you haven't heard back after 3 business days, please contact your
              organization administrator.
            </p>
          </div>

          {/* Sign Out Button */}
          <button
            onClick={handleSignOut}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-white/60 text-sm mt-6">
          Questions? Contact your organization administrator.
        </p>
      </div>
    </div>
  );
}
